import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import poko_waist from '../assets/poko_handsInWaist.png';
import poko_jump from '../assets/poko_jump.png';
import './ShaftGamePage.css';

function importAllSigns() {
	const context = require.context('../assets/signs/number', false, /\.(png|jpe?g|svg)$/);
	const signs = {};
	context.keys().forEach((key) => {
		const fileName = key.replace('./', '').replace(/\.(png|jpe?g|svg)$/, '');
		signs[fileName] = context(key);
	});
	return signs;
}

const allSigns = importAllSigns();

// const signs = [
// 	{ image: allSigns['1'], value: '1' },
// 	{ image: allSigns['2'], value: '2' },
// 	{ image: allSigns['3'], value: '3' },
// 	{ image: allSigns['4'], value: '4' },
// 	{ image: allSigns['5'], value: '5' },
// 	{ image: allSigns['6'], value: '6' },
// 	{ image: allSigns['7'], value: '7' },
// 	{ image: allSigns['8'], value: '8' }
// ];

// Base dimensions for reference (will be scaled based on container width)
const BASE_GAME_WIDTH = 800;

// Physics constants are tuned per 50ms tick; the rAF loop scales them by
// elapsed time so game speed is independent of display refresh rate.
const TICK_MS = 50;
const BASE_CHARACTER_WIDTH = 80;
const BASE_CHARACTER_HEIGHT = 100;
const BASE_PLATFORM_WIDTH = 150;
const BASE_PLATFORM_HEIGHT = 30;

// Minimum sizes to ensure visibility
const MIN_CHARACTER_WIDTH = 50;
const MIN_CHARACTER_HEIGHT = 60;
const MIN_PLATFORM_WIDTH = 100;
const MIN_PLATFORM_HEIGHT = 25;

// Scale factors
const getGameDimensions = (containerWidth) => {
	const scale = Math.min(containerWidth / BASE_GAME_WIDTH, 1);

	// Calculate scaled dimensions with minimum bounds
	const characterWidth = Math.max(BASE_CHARACTER_WIDTH * scale, MIN_CHARACTER_WIDTH);
	const characterHeight = Math.max(BASE_CHARACTER_HEIGHT * scale, MIN_CHARACTER_HEIGHT);
	const platformWidth = Math.max(BASE_PLATFORM_WIDTH * scale, MIN_PLATFORM_WIDTH);
	const platformHeight = Math.max(BASE_PLATFORM_HEIGHT * scale, MIN_PLATFORM_HEIGHT);

	// Adjust game width to ensure proper spacing with minimum sizes
	const gameWidth = BASE_GAME_WIDTH * scale;
	const minX = Math.max(50 * scale, 30); // Minimum side padding
	const maxX = gameWidth - platformWidth - minX;

	return {
		GAME_WIDTH: gameWidth,
		CHARACTER_WIDTH: characterWidth,
		CHARACTER_HEIGHT: characterHeight,
		PLATFORM_WIDTH: platformWidth,
		PLATFORM_HEIGHT: platformHeight,
		MIN_X: minX,
		MAX_X: maxX,
		SCALE: scale
	};
};

const alphabetsAndNumbers = {
	'1': { sign: allSigns['1'], type: 'number' },
	'2': { sign: allSigns['2'], type: 'number' },
	'3': { sign: allSigns['3'], type: 'number' },
	'4': { sign: allSigns['4'], type: 'number' },
	'5': { sign: allSigns['5'], type: 'number' },
	'6': { sign: allSigns['6'], type: 'number' },
	'7': { sign: allSigns['7'], type: 'number' },
	'8': { sign: allSigns['8'], type: 'number' }
};

function ShaftGamePage() {
	const navigate = useNavigate();
	const [dimensions, setDimensions] = useState(getGameDimensions(800));
	const gameAreaRef = React.useRef(null);
	const [firstVisit, setFirstVisit] = useState(true);
	const [isStarted, setIsStarted] = useState(false);
	const [learntSigns, setLearntSigns] = useState([]);
	const [selectedOption, setSelectedOption] = useState('all'); // 'all' or 'learnt'
	const [showInstructions, setShowInstructions] = useState(false);
	const [gameStarted, setGameStarted] = useState(false);  // New state for actual game start
	const [wasGameRunning, setWasGameRunning] = useState(false);

	// Create audio context and buffers
	const audioContext = useRef(null);
	const correctBuffer = useRef(null);
	const incorrectBuffer = useRef(null);

	// Initialize audio context and load sounds
	useEffect(() => {
		// Create audio context
		audioContext.current = new (window.AudioContext || window.webkitAudioContext)();

		// Load sounds
		const loadSound = async (url) => {
			try {
				const response = await fetch(url);
				const arrayBuffer = await response.arrayBuffer();
				return await audioContext.current.decodeAudioData(arrayBuffer);
			} catch (error) {
				console.error('Error loading sound:', error);
				return null;
			}
		};

		// Load both sounds
		Promise.all([
			loadSound('/sounds/correct.mp3'),
			loadSound('/sounds/incorrect.mp3')
		]).then(([correct, incorrect]) => {
			correctBuffer.current = correct;
			incorrectBuffer.current = incorrect;
			console.log('Sounds loaded successfully');
		}).catch(error => {
			console.error('Error loading sounds:', error);
		});

		// Cleanup
		return () => {
			if (audioContext.current) {
				audioContext.current.close();
			}
		};
	}, []);

	const playSound = (buffer) => {
		if (audioContext.current && buffer) {
			try {
				const source = audioContext.current.createBufferSource();
				source.buffer = buffer;
				source.connect(audioContext.current.destination);
				source.start(0);
			} catch (error) {
				console.error('Error playing sound:', error);
			}
		}
	};

	const [gameState, setGameState] = useState({
		character: {
			x: dimensions.GAME_WIDTH / 2 - dimensions.CHARACTER_WIDTH / 2,
			y: 0,
			velocityY: 0,
			isJumping: false,
			isOnPlatform: false,
			movingLeft: false,
			movingRight: false,
			currentPlatformId: null
		},
		platforms: [],
		score: 0,
		lives: 5,
		speed: dimensions.GAME_WIDTH * 0.0025,
		currentLevel: 1,
		gameOver: false,
		feedback: { type: '', message: '', show: false },
		targetSign: null,
		targetLetter: null,
		isPaused: false,
		showingNextQuestion: false,
		nextQuestionTimer: null,
		correctJumps: 0,
		requiredJumps: 3,
		visitedPlatforms: [],
		displayedSigns: []
	});

	// Add resize handler
	useEffect(() => {
		const handleResize = () => {
			if (gameAreaRef.current) {
				const containerWidth = gameAreaRef.current.offsetWidth;
				const newDimensions = getGameDimensions(containerWidth);
				setDimensions(newDimensions);

				// Update all platform and character positions
				setGameState(prev => {
					const screenHeight = newDimensions.GAME_WIDTH * 0.75;

					return {
						...prev,
						character: {
							...prev.character,
							x: (prev.character.x / dimensions.GAME_WIDTH) * newDimensions.GAME_WIDTH,
							y: (prev.character.y / dimensions.GAME_WIDTH) * newDimensions.GAME_WIDTH,
						},
						platforms: prev.platforms.map(platform => {
							// Calculate new positions based on stored percentages
							const screenIndex = Math.floor(platform.yPercent / 100);
							const screenY = platform.yPercent % 100;

							return {
								...platform,
								x: toPixels(platform.xPercent, newDimensions.GAME_WIDTH - newDimensions.PLATFORM_WIDTH),
								y: (screenIndex * screenHeight) + toPixels(screenY, screenHeight)
							};
						}),
						speed: newDimensions.GAME_WIDTH * 0.0025 // Update speed based on new dimensions
					};
				});
			}
		};

		window.addEventListener('resize', handleResize);
		handleResize(); // Initial calculation
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Convert pixel position to percentage
	const toPercentage = (pixelPos, total) => (pixelPos / total) * 100;

	// Convert percentage to pixel position
	const toPixels = (percentage, total) => (percentage * total) / 100;

	const handleFirstVisit = () => {
		setIsStarted(true);
		handleStartGame();
		if (firstVisit)
			setShowInstructions(true)
		else
			setGameStarted(true);
	}

	const handleFirstVisitStartGame = () => {
		setShowInstructions(false);
		setGameStarted(true);
		setFirstVisit(false);
	}

	const handleStartGame = () => {
		// Filter available symbols based on selected option
		const allSymbols = Object.keys(alphabetsAndNumbers);
		let availableSymbols;
		let availableSignsMap;

		if (selectedOption === 'learnt') {
			if (learntSigns.length === 0) {
				availableSymbols = allSymbols;
				availableSignsMap = alphabetsAndNumbers;
			} else {
				const upperLearntSigns = learntSigns.map(sign => sign.toUpperCase());
				availableSymbols = allSymbols.filter(symbol => upperLearntSigns.includes(symbol));
				availableSignsMap = Object.fromEntries(
					Object.entries(alphabetsAndNumbers).filter(([key]) => upperLearntSigns.includes(key))
				);
				if (availableSymbols.length === 0) {
					availableSymbols = allSymbols;
					availableSignsMap = alphabetsAndNumbers;
				}
			}
		} else {
			availableSymbols = allSymbols;
			availableSignsMap = alphabetsAndNumbers;
		}

		const { platforms, targetLetter, targetSign } = generatePlatforms(availableSymbols, availableSignsMap);

		setGameState({
			character: {
				x: dimensions.GAME_WIDTH / 2 - dimensions.CHARACTER_WIDTH / 2,
				y: 0,
				velocityY: 0,
				velocityX: 0,
				isJumping: false,
				isOnPlatform: false,
				movingLeft: false,
				movingRight: false,
				currentPlatformId: null
			},
			platforms,
			score: 0,
			lives: 5,
			speed: dimensions.GAME_WIDTH * 0.0025,
			currentLevel: 1,
			gameOver: false,
			feedback: { type: '', message: '', show: false },
			targetSign,
			targetLetter,
			isPaused: false,
			showingNextQuestion: false,
			nextQuestionTimer: null,
			correctJumps: 0,
			requiredJumps: 3,
			visitedPlatforms: [],
			displayedSigns: [targetLetter]
		});
	};

	const generatePlatforms = (availableSymbols, signsMap = alphabetsAndNumbers) => {
		const platforms = [];
		// Ensure we have at least 2 symbols for variety
		if (availableSymbols.length < 2) {
			availableSymbols = Object.keys(alphabetsAndNumbers);
			signsMap = alphabetsAndNumbers;
		}

		// Get the current displayed signs from gameState
		const currentDisplayedSigns = gameState.displayedSigns || [];
		
		// Filter out numbers that haven't been used yet
		let unusedNumbers = availableSymbols.filter(num => !currentDisplayedSigns.includes(num));
		
		// If all numbers have been used, reset the unused numbers list
		if (unusedNumbers.length === 0) {
			unusedNumbers = [...availableSymbols];
		}
		
		// Randomly select a new target number from unused numbers
		const newTargetLetter = unusedNumbers[Math.floor(Math.random() * unusedNumbers.length)];
		const newTargetSign = signsMap[newTargetLetter].sign;

		// Define base platform positions in percentages with increased vertical gaps
		const patternScreens = [
			[ // Screen 1
				[15, 85, true],    // [xPercent, yPercent, isSafe]
				[75, 60, true],
				[45, 35, false],
				[15, 15, false],
			],
			[ // Screen 2
				[75, 85, true],
				[25, 60, false],
				[55, 35, true],
				[85, 15, false],
			],
			[ // Screen 3
				[35, 85, false],
				[65, 60, true],
				[15, 35, false],
				[55, 15, true],
			],
			[ // Screen 4
				[45, 85, true],
				[15, 60, false],
				[85, 35, true],
				[35, 15, false],
			],
			[ // Screen 5
				[65, 85, false],
				[25, 60, true],
				[75, 35, false],
				[15, 15, true],
			],
			[ // Screen 6
				[25, 85, true],
				[85, 60, false],
				[35, 35, true],
				[65, 15, false],
			]
		];

		// Generate multiple screens of platforms
		const numScreens = 8;
		const screenHeight = dimensions.GAME_WIDTH * 0.75;
		const totalHeight = screenHeight * numScreens;

		// Add initial platform at the bottom first
		platforms.push({
			id: 'initial',
			x: dimensions.GAME_WIDTH / 2 - dimensions.PLATFORM_WIDTH / 2,
			y: totalHeight - dimensions.PLATFORM_HEIGHT - 20,
			xPercent: 50,
			yPercent: 90,
			word: newTargetLetter,
			isSafe: true
		});

		// Start from the bottom and work upwards, but only generate platforms for the bottom half initially
		for (let screen = numScreens - 1; screen >= numScreens / 2; screen--) {
			// Randomly select a pattern for each screen
			const pattern = patternScreens[Math.floor(Math.random() * patternScreens.length)];

			pattern.forEach(([baseXPercent, baseYPercent, shouldBeSafe]) => {
				// Add some randomization to x position (±10% within safe bounds)
				const xVariation = (Math.random() - 0.5) * 20; // ±10%
				let xPercent = Math.max(10, Math.min(90, baseXPercent + xVariation));

				// Add slight variation to y position (±5% within safe bounds)
				const yVariation = (Math.random() - 0.5) * 6; // ±3%
				const yPercent = Math.max(10, Math.min(90, baseYPercent + yVariation));

				const x = toPixels(xPercent, dimensions.GAME_WIDTH - dimensions.PLATFORM_WIDTH);
				// Calculate y position from bottom up, ensuring platforms are above the initial platform
				const y = totalHeight - ((screen * screenHeight) + toPixels(yPercent, screenHeight)) - dimensions.PLATFORM_HEIGHT;

				let symbol;
				if (shouldBeSafe) {
					symbol = newTargetLetter;
				} else {
					// For unsafe platforms, use any number EXCEPT the target
					const wrongNumbers = availableSymbols.filter(s => s !== newTargetLetter);
					symbol = wrongNumbers[Math.floor(Math.random() * wrongNumbers.length)];
				}

				platforms.push({
					id: Math.random().toString(36).substr(2, 9),
					x,
					y,
					xPercent,
					yPercent: yPercent + (screen * 100),
					word: symbol,
					isSafe: shouldBeSafe
				});
			});
		}

		// Sort platforms by y position in descending order (bottom to top)
		platforms.sort((a, b) => b.y - a.y);

		return { platforms, targetLetter: newTargetLetter, targetSign: newTargetSign };
	};

	const updateGame = (dtFactor = 1) => {
		if (gameState.gameOver || gameState.lives <= 0 || gameState.isPaused || showInstructions || gameState.showingNextQuestion) {
			return;
		}

		setGameState(prev => {
			// If game is over, return current state without updates
			if (prev.lives <= 0) {
				return {
					...prev,
					gameOver: true,
					character: {
						...prev.character,
						movingLeft: false,
						movingRight: false,
						velocityY: 0,
						isJumping: false
					}
				};
			}

			// Get the current available symbols based on selected option
			let availableSymbols;
			let availableSignsMap;

			if (selectedOption === 'learnt') {
				if (learntSigns.length === 0) {
					availableSymbols = Object.keys(alphabetsAndNumbers);
					availableSignsMap = alphabetsAndNumbers;
				} else {
					const upperLearntSigns = learntSigns.map(sign => sign.toUpperCase());
					availableSymbols = Object.keys(alphabetsAndNumbers).filter(symbol => upperLearntSigns.includes(symbol));
					availableSignsMap = Object.fromEntries(
						Object.entries(alphabetsAndNumbers).filter(([key]) => upperLearntSigns.includes(key))
					);

					if (availableSymbols.length < 2) {
						availableSymbols = Object.keys(alphabetsAndNumbers);
						availableSignsMap = alphabetsAndNumbers;
					}
				}
			} else {
				availableSymbols = Object.keys(alphabetsAndNumbers);
				availableSignsMap = alphabetsAndNumbers;
			}

			const screenHeight = dimensions.GAME_WIDTH * 0.75;
			const totalHeight = screenHeight * 8; // 8 screens total

			const newPlatforms = prev.platforms
				.map(platform => ({
					...platform,
					y: platform.y - prev.speed * dtFactor
				}))
				.filter(platform => platform.y > -20);

			// Check if we need to add new platforms at the bottom
			const maxY = Math.max(...newPlatforms.map(p => p ? p.y : 0));

			if (maxY < totalHeight - screenHeight) {
				const y = totalHeight - dimensions.PLATFORM_HEIGHT - 20;
				const x = dimensions.MIN_X + Math.random() * (dimensions.MAX_X - dimensions.MIN_X);

				// Check if there are any visible platforms with the current target
				const visiblePlatforms = newPlatforms.filter(p => p && p.y >= 0 && p.y <= screenHeight);
				const targetPlatforms = visiblePlatforms.filter(p => p && p.word === prev.targetLetter);

				// Force at least 2 correct platforms to be visible at all times
				const shouldBeSafe = targetPlatforms.length < 2 || Math.random() < 0.35;
				let word;
				if (shouldBeSafe) {
					// Always use current target for safe platforms
					word = prev.targetLetter;
				} else {
					// For unsafe platforms, use any number EXCEPT the target
					const wrongNumbers = availableSymbols.filter(s => s !== prev.targetLetter);
					word = wrongNumbers[Math.floor(Math.random() * wrongNumbers.length)];
				}

				// Ensure all required properties are set
				const newPlatform = {
					id: Math.random().toString(36).substr(2, 9),
					x,
					y,
					word,
					isSafe: shouldBeSafe,
					xPercent: (x / (dimensions.GAME_WIDTH - dimensions.PLATFORM_WIDTH)) * 100,
					yPercent: (y / (dimensions.GAME_WIDTH * 0.75)) * 100
				};

				newPlatforms.push(newPlatform);
			}

			// Additional safety check after platform generation
			const visiblePlatforms = newPlatforms.filter(p => p && p.y >= 0 && p.y <= screenHeight);
			const targetPlatforms = visiblePlatforms.filter(p => p && p.word === prev.targetLetter);

			// If there aren't enough target platforms on screen, convert upcoming
			// platforms that are still below the visible area — never visible ones,
			// so the player doesn't see a platform switch its number mid-game
			if (targetPlatforms.length < 2) {
				const upcomingPlatforms = newPlatforms
					.filter(p => p && p.word !== prev.targetLetter && p.y > screenHeight)
					.sort((a, b) => a.y - b.y); // soonest to scroll into view first

				const platformsToConvert = upcomingPlatforms.slice(0, 2 - targetPlatforms.length);
				platformsToConvert.forEach(platform => {
					const index = newPlatforms.findIndex(p => p && p.id === platform.id);
					if (index !== -1) {
						newPlatforms[index] = {
							...newPlatforms[index],
							word: prev.targetLetter,
							isSafe: true
						};
					}
				});
			}

			const gravity = 0.5;
			const maxFallSpeed = 10;
			const maxMoveSpeed = 10;
			const moveAccel = 2.5;
			const moveFriction = 3;

			let newVelocityY = prev.character.velocityY;
			let newVelocityX = prev.character.velocityX || 0;
			let newY = prev.character.y;
			let newX = prev.character.x;
			let isJumping = prev.character.isJumping;
			let isOnPlatform = false;
			let currentPlatformId = prev.character.currentPlatformId;
			let wasOnPlatform = prev.character.isOnPlatform;
			let newScore = prev.score;
			let newLives = prev.lives;
			let newFeedback = prev.feedback;
			let newTargetLetter = prev.targetLetter;
			let newTargetSign = prev.targetSign;
			let isPaused = prev.isPaused;
			let showingNextQuestion = prev.showingNextQuestion;
			let nextQuestionTimer = prev.nextQuestionTimer;
			let correctJumps = prev.correctJumps;
			let requiredJumps = prev.requiredJumps;

			newVelocityY = Math.min(newVelocityY + gravity * dtFactor, maxFallSpeed);
			newY += newVelocityY * dtFactor;

			// Update horizontal velocity: accelerate toward held direction,
			// otherwise decay to a stop so movement feels responsive, not on/off
			if (prev.character.movingLeft && !prev.character.movingRight) {
				newVelocityX = Math.max(newVelocityX - moveAccel * dtFactor, -maxMoveSpeed);
			} else if (prev.character.movingRight && !prev.character.movingLeft) {
				newVelocityX = Math.min(newVelocityX + moveAccel * dtFactor, maxMoveSpeed);
			} else if (newVelocityX !== 0) {
				const decay = moveFriction * dtFactor;
				newVelocityX = Math.abs(newVelocityX) <= decay ? 0 : newVelocityX - Math.sign(newVelocityX) * decay;
			}
			newX = Math.max(0, Math.min(dimensions.GAME_WIDTH - dimensions.CHARACTER_WIDTH, newX + newVelocityX * dtFactor));

			// Check if character is falling off a platform
			const isFallingOffPlatform = wasOnPlatform &&
				!newPlatforms.some(platform => {
					const isWithinHorizontalBounds =
						newX + dimensions.CHARACTER_WIDTH >= platform.x &&
						newX <= platform.x + dimensions.PLATFORM_WIDTH;
					const isFallingOntoPlatform =
						newY + dimensions.CHARACTER_HEIGHT >= platform.y &&
						newY <= platform.y + dimensions.PLATFORM_HEIGHT;
					return isWithinHorizontalBounds && isFallingOntoPlatform;
				});

			// If falling off platform or moving horizontally off it, prevent moving back onto it
			if (isFallingOffPlatform || (wasOnPlatform &&
				!newPlatforms.some(platform =>
					platform.id === prev.character.currentPlatformId &&
					newX + dimensions.CHARACTER_WIDTH >= platform.x &&
					newX <= platform.x + dimensions.PLATFORM_WIDTH))) {
				wasOnPlatform = false;
				currentPlatformId = null;

				// Only allow Y position to decrease (move downward)
				if (newY < prev.character.y) {
					newY = prev.character.y;
				}
			}

			const platformCollision = newPlatforms.find(platform => {
				const isWithinHorizontalBounds =
					newX + dimensions.CHARACTER_WIDTH >= platform.x &&
					newX <= platform.x + dimensions.PLATFORM_WIDTH;

				// Use bottom of character (adjusted by 20px) and top of platform for collision detection
				const characterBottom = Math.floor(newY + dimensions.CHARACTER_HEIGHT - 20); // Reduce bottom by 20px
				const platformTop = Math.floor(platform.y);

				// More forgiving collision detection but precise landing
				const isFallingOntoPlatform =
					characterBottom >= platformTop - 5 && // Detection buffer
					characterBottom <= platformTop + 10 && // Landing threshold
					(newVelocityY >= 0 || wasOnPlatform); // Allow standing

				const isHigherPlatform = platformTop < Math.floor(newY);

				return isWithinHorizontalBounds &&
					isFallingOntoPlatform &&
					(!wasOnPlatform || !isHigherPlatform);
			});

			if (platformCollision) {
				// Ensure precise integer positioning, adjusted for the 20px reduction
				newY = Math.floor(platformCollision.y - dimensions.CHARACTER_HEIGHT + 20);
				newVelocityY = 0;
				isJumping = false;
				isOnPlatform = true;

				// Check if character is still within platform bounds
				if (wasOnPlatform && platformCollision.id === prev.character.currentPlatformId) {
					const isStillOnPlatform =
						newX + dimensions.CHARACTER_WIDTH >= platformCollision.x &&
						newX <= platformCollision.x + dimensions.PLATFORM_WIDTH;

					if (!isStillOnPlatform) {
						isOnPlatform = false;
						currentPlatformId = null;
					}
				}

				if (currentPlatformId !== platformCollision.id) {
					currentPlatformId = platformCollision.id;

					if (platformCollision.word !== prev.targetLetter) {
						newLives = prev.lives - 1;
						newFeedback = {
							type: 'error',
							message: `Oops! Wrong platform. You lost 1 life.`,
							show: true
						};

						// Play incorrect sound
						playSound(incorrectBuffer.current);

						// Show feedback for 2 seconds
						setTimeout(() => {
							setGameState(prev => ({
								...prev,
								feedback: { ...prev.feedback, show: false }
							}));
						}, 2000);
					} else {
						// Play correct sound every time landing on correct platform
						playSound(correctBuffer.current);

						if (!prev.visitedPlatforms.includes(platformCollision.id) && !prev.showingNextQuestion) {
							// Only count score if not showing next question
							newScore = prev.score + 10;
							const newCorrectJumps = prev.correctJumps + 1;

							if (newCorrectJumps >= prev.requiredJumps) {
								// Show success feedback immediately
								newFeedback = {
									type: 'success',
									message: `Very Good!`,
									show: true
								};
								correctJumps = newCorrectJumps;

								// Wait 1 second before pausing and showing next question
								setTimeout(() => {
									// First, pause the game and show the next question
									setGameState(prev => ({
										...prev,
										isPaused: true,
										showingNextQuestion: true,
										character: {
											...prev.character,
											movingLeft: false,
											movingRight: false,
											velocityY: 0,
											isJumping: false,
											isOnPlatform: false,
											currentPlatformId: null
										}
									}));

									// Then, after a short delay, update the game state with new target
									setTimeout(() => {
										setGameState(prev => {
											const newTarget = generatePlatforms(availableSymbols, availableSignsMap);
											return {
												...prev,
												correctJumps: 0,
												targetLetter: newTarget.targetLetter,
												targetSign: newTarget.targetSign,
												platforms: newTarget.platforms,
												visitedPlatforms: [],
												displayedSigns: [...prev.displayedSigns, newTarget.targetLetter],
												character: {
													...prev.character,
													x: dimensions.GAME_WIDTH / 2 - dimensions.CHARACTER_WIDTH / 2,
													y: 0
												}
											};
										});

										// Show next question briefly before resuming
										const timer = setTimeout(() => {
											setGameState(prev => ({
												...prev,
												isPaused: false,
												showingNextQuestion: false,
												nextQuestionTimer: null
											}));
										}, 1500);

										setGameState(prev => ({
											...prev,
											nextQuestionTimer: timer
										}));
									}, 100);
								}, 1000);
							} else {
								newFeedback = {
									type: 'success',
									message: `Correct! + 10 points!`,
									show: true
								};
								correctJumps = newCorrectJumps;
							}
						}
					}
				}
			} else {
				currentPlatformId = null;
				newFeedback = { ...prev.feedback, show: false };
			}

			if (newY > 500) {
				const newLives = prev.lives - 1;
				const feedbackMessage = {
					type: 'error',
					message: 'Oops! You fall off and lost 1 life.',
					show: true
				};

				// Play incorrect sound
				playSound(incorrectBuffer.current);

				// Check if this fall causes game over
				if (newLives <= 0) {
					return {
						...prev,
						lives: newLives,
						gameOver: true,
						character: {
							...prev.character,
							movingLeft: false,
							movingRight: false,
							velocityY: 0,
							isJumping: false,
							isOnPlatform: false,
							currentPlatformId: null
						}
					};
				}

				// Set a timeout to hide the feedback message
				setTimeout(() => {
					setGameState(prev => ({
						...prev,
						feedback: { ...prev.feedback, show: false }
					}));
				}, 2000);

				return {
					...prev,
					lives: newLives,
					feedback: feedbackMessage,
					character: {
						...prev.character,
						x: dimensions.GAME_WIDTH / 2 - dimensions.CHARACTER_WIDTH / 2,
						y: 0,
						velocityY: 0,
						velocityX: 0,
						isJumping: false,
						isOnPlatform: false,
						currentPlatformId: null
					}
				}
			}

			return {
				...prev,
				score: newScore,
				lives: newLives,
				feedback: newFeedback,
				targetLetter: newTargetLetter,
				targetSign: newTargetSign,
				isPaused: isPaused,
				showingNextQuestion: showingNextQuestion,
				nextQuestionTimer: nextQuestionTimer,
				correctJumps: correctJumps,
				visitedPlatforms: platformCollision && platformCollision.word === prev.targetLetter ?
					[...new Set([...prev.visitedPlatforms, platformCollision.id])] :
					prev.visitedPlatforms,
				character: {
					...prev.character,
					x: newX,
					y: newY,
					velocityY: newVelocityY,
					velocityX: newVelocityX,
					isJumping,
					isOnPlatform,
					currentPlatformId
				},
				platforms: newPlatforms,
				gameOver: newLives <= 0
			};
		});
	};

	const handleKeyDown = (e) => {
		if (gameState.gameOver || gameState.isPaused || gameState.showingNextQuestion) return;

		setGameState(prev => ({
			...prev,
			character: {
				...prev.character,
				movingLeft: e.key === 'ArrowLeft' || prev.character.movingLeft,
				movingRight: e.key === 'ArrowRight' || prev.character.movingRight
			}
		}));
	};

	const handleKeyUp = (e) => {
		if (gameState.gameOver || gameState.isPaused || gameState.showingNextQuestion) return;

		setGameState(prev => ({
			...prev,
			character: {
				...prev.character,
				movingLeft: e.key === 'ArrowLeft' ? false : prev.character.movingLeft,
				movingRight: e.key === 'ArrowRight' ? false : prev.character.movingRight
			}
		}));
	};

	const handleTouchStart = (direction) => {
		if (gameState.gameOver || gameState.isPaused || gameState.showingNextQuestion) return;

		setGameState(prev => ({
			...prev,
			character: {
				...prev.character,
				movingLeft: direction === 'left' ? true : prev.character.movingLeft,
				movingRight: direction === 'right' ? true : prev.character.movingRight
			}
		}));
	};

	const handleTouchEnd = (direction) => {
		if (gameState.gameOver || gameState.isPaused || gameState.showingNextQuestion) return;

		setGameState(prev => ({
			...prev,
			character: {
				...prev.character,
				movingLeft: direction === 'left' ? false : prev.character.movingLeft,
				movingRight: direction === 'right' ? false : prev.character.movingRight
			}
		}));
	};

	const handleShowInstructions = () => {
		// Always pause the game when showing instructions
		setGameState(prev => ({
			...prev,
			isPaused: true,
			character: {
				...prev.character,
				movingLeft: false,
				movingRight: false,
				velocityY: 0
			}
		}));
		setShowInstructions(true);
	};

	const handleCloseInstructions = (e) => {
		e.stopPropagation();
		setShowInstructions(false);
		// Always resume the game when closing instructions
		setGameState(prev => ({
			...prev,
			isPaused: false
		}));
	};

	useEffect(() => {
		if (!gameState.gameOver && !gameState.isPaused && !gameState.showingNextQuestion) {
			if (gameStarted) {
				window.addEventListener('keydown', handleKeyDown);
				window.addEventListener('keyup', handleKeyUp);

				// Run the game loop in sync with the display refresh rate,
				// scaling physics by elapsed time so speed stays the same.
				// dtFactor is capped so a background tab doesn't cause a
				// giant step that tunnels through platforms.
				let rafId;
				let lastTime = performance.now();
				const gameLoop = (now) => {
					const dtFactor = Math.min((now - lastTime) / TICK_MS, 1.5);
					lastTime = now;
					updateGame(dtFactor);
					rafId = requestAnimationFrame(gameLoop);
				};
				rafId = requestAnimationFrame(gameLoop);

				return () => {
					cancelAnimationFrame(rafId);
					window.removeEventListener('keydown', handleKeyDown);
					window.removeEventListener('keyup', handleKeyUp);
				};
			}
		}
	}, [gameState.gameOver, gameStarted, gameState.isPaused, gameState.showingNextQuestion]);

	useEffect(() => {
		if (gameState.feedback.show) {
			const timer = setTimeout(() => {
				setGameState(prev => ({
					...prev,
					feedback: { ...prev.feedback, show: false }
				}));
			}, 2500);
			return () => clearTimeout(timer);
		}
	}, [gameState.feedback.show]);

	// Add useEffect to load learnt signs from localStorage
	useEffect(() => {
		const stored = localStorage.getItem('learntSigns');
		if (stored) {
			try {
				const parsed = JSON.parse(stored);
				setLearntSigns(parsed.number || []);
			} catch (err) {
				console.error('Failed to parse learnt signs from localStorage', err);
			}
		}
	}, []);

	const InstructionsModal = () => (
		<div className="instructions-modal-overlay" onClick={handleCloseInstructions}>
			<div className="instructions-modal" onClick={e => e.stopPropagation()}>
				{!firstVisit && (
					<button className="close-button" onClick={handleCloseInstructions}>×</button>
				)}
				<h2>🧠 How to Play?</h2>

				<div className="instruction-section">
					<h3>1. Your Goal</h3>
					<p>• Use your keyboard or ◀︎ and ▶︎ below to move</p>
					<p>• Jump on platforms with the correct sign number</p>
					<p>• Make <strong>3 correct jumps</strong> to unlock the next number</p>
					<p>• Avoid platforms with wrong numbers!</p>
				</div>

				<div className="instruction-section">
					<h3>2. Counting Score</h3>
					<p>• ➕ <strong>10 points</strong> for jumping on correct platforms</p>
					<p>• <strong>Lose 1 life ❤️</strong> for jumping on wrong platforms</p>
					<p>• Start with 5 lives ❤️❤️❤️❤️❤️ - lose all and game over!</p>
				</div>

				{firstVisit ? (
					<button className="game-btn" onClick={handleFirstVisitStartGame}>
						Got it, let’s go!
					</button>
				) : (
					<button className="game-btn" onClick={handleCloseInstructions}>
						Close
					</button>
				)}
			</div>
		</div>
	);

	const EndGameModal = () => (
		<div className="instructions-modal-overlay">
			<div className="instructions-modal" onClick={(e) => e.stopPropagation()}>
				<div className='page-title'>
					<h2>Game Over!</h2>
					<h6>Your final score: {gameState.score} 🎉</h6>
				</div>
				<div className="displayed-signs">
					<h5 className="review-title">🧐 Let’s review what you matched:</h5>
					<div className="signs-grid">
						{[...new Set(gameState.displayedSigns)].map((letter, index) => (
							<div key={index} className="sign-item">
								<img
									src={alphabetsAndNumbers[letter].sign}
									alt={`Sign for ${letter}`}
									className="sign-image"
								/>
								<span className="sign-number">{letter}</span>
							</div>
						))}
					</div>
				</div>
				<button onClick={() => navigate('/learn')} className="game-btn revisit-btn">🧠 Revisit in Learn Page</button>
				<div className='row-wrapper'>
					<button onClick={handleStartGame} className="game-btn">🔁 Play Again</button>
					<button onClick={() => navigate('/games')} className="game-btn">🎮 Try Another Game</button>
				</div>
			</div>
		</div>
	);

	if (!isStarted) {
		return (
			<div className="shaft-game-page page">
				<div className="white-wrapper container">
					<div className="page-title">
						<button className="game-back-btn" onClick={() => navigate('/games')}>⬅︎ Back to Games</button>
						<h1>🎯 Shaft Game</h1>
					</div>

					<div className="speech-section">
						<img src={poko_waist} alt="Poko" className="poko" />
						<div className="speech-bubble-right">
							<div className="game-instruction">
								Welcome to the Shaft Game! 🎉<br />
								Jump on platforms with the correct sign numbers and avoid the wrong ones.<br />
								🙌 Ready to test your Auslan skills?
							</div>

							<div className="game-sign-option">
								<p>Choose your sign set:</p>
								<div className="radio-group">
									<label>
										<input
											type="radio"
											name="signSet"
											value="all"
											checked={selectedOption === 'all'}
											onChange={() => setSelectedOption('all')}
										/>
										All signs
										<div className="option-description">
											🌀 Play with all the signs, even the ones you haven't learned yet!
										</div>
									</label>

									<label className={learntSigns.length < 5 ? 'disabled' : ''}>
										<input
											type="radio"
											name="signSet"
											value="learnt"
											disabled={learntSigns.length < 5}
											checked={selectedOption === 'learnt'}
											onChange={() => setSelectedOption('learnt')}
										/>
										Learnt signs
										{learntSigns.length < 5 ? (
											<div className="option-description disabled">
												🧠 You've learnt <strong>{learntSigns.length} number signs</strong> so far! <br />
												📖 Keep learning — unlock this mode after learning at least <strong>5 number signs</strong>.<br />
												👉 <button onClick={() => navigate('/learn')}>Go to Learn</button>
											</div>
										) : (
											<div className="option-description">
												✅ You've learnt <strong>{learntSigns.length} signs</strong>! <br />
												🥳 Pick this mode to practice signs you already know. <br />
												👉 <button onClick={() => navigate('/learn')}>Want to learn more?</button>
											</div>
										)}
									</label>
								</div>
							</div>

							<button className="game-start-button" onClick={handleFirstVisit}>
								Start!
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="shaft-game-page page">
			<div className="white-wrapper container">
				<div className="page-title">
					<button className="game-back-btn" onClick={() => navigate('/games')}>⬅︎ Back to Games</button>
					<div className="title-container">
						<h2>🎯 Shaft Game</h2>
						<button
							className="help-button"
							onClick={handleShowInstructions}
							title="Show Instructions"
						>
							❔
						</button>
					</div>
				</div>

				{gameStarted && (
					<div className="game-container">
						<div className="game-info-container">
							<div className="game-info-row">
								<div className="sign-section">
									<h4>Jump on the number of:</h4>
									<img
										src={gameState.targetSign}
										alt="Target Sign"
										className="sign-image game-info"
									/>
									<div className="progress-indicator">
										{Array.from({ length: gameState.requiredJumps }, (_, i) => (
											<div
												key={i}
												className={`progress-dot ${i < gameState.correctJumps ? 'completed' : ''}`}
											>
												{i + 1}
											</div>
										))}
									</div>
								</div>
								<div className="game-stats">
									<div className="shaft-score">Score: {gameState.score}</div>
									<div className="shaft-lives">{"❤️".repeat(gameState.lives)}</div>
								</div>
							</div>
						</div>
						<div className="game-area-container">
							<div ref={gameAreaRef} className="shaft-game-area">
								{gameState.feedback.show && (
									<div className={`feedback-box ${gameState.feedback.type}`}>
										{gameState.feedback.message}
									</div>
								)}
								{gameState.showingNextQuestion && (
									<div className="next-question-overlay">
										<div className="next-question-content">
											<h3>Next Number:</h3>
											<img
												src={gameState.targetSign}
												alt="Next Target Sign"
												className="next-question-sign"
											/>
											<p>Get ready!</p>
										</div>
									</div>
								)}
								<div
									className="shaft-character"
									style={{
										transform: `translate(${gameState.character.x}px, ${gameState.character.y}px) scaleY(${gameState.character.velocityY > 0 ? 1 : 0.9})`,
										width: `${dimensions.CHARACTER_WIDTH}px`,
										height: `${dimensions.CHARACTER_HEIGHT}px`
									}}
								>
									<img src={poko_jump} alt="Jumping character" className="character-image" />
								</div>
								{gameState.platforms.map((platform) => (
									<div
										key={platform.id}
										className="platform"
										style={{
											transform: `translate(${platform.x}px, ${platform.y}px)`,
											width: `${dimensions.PLATFORM_WIDTH}px`,
											height: `${dimensions.PLATFORM_HEIGHT}px`
										}}
									>
										<div className="platform-content">
											{platform.word}
										</div>
									</div>
								))}
							</div>
							<div className="touch-controls">
								<button
									className="touch-button left-button"
									onTouchStart={() => handleTouchStart('left')}
									onTouchEnd={() => handleTouchEnd('left')}
									onMouseDown={() => handleTouchStart('left')}
									onMouseUp={() => handleTouchEnd('left')}
									onMouseLeave={() => handleTouchEnd('left')}
								>
									◀︎
								</button>
								<button
									className="touch-button right-button"
									onTouchStart={() => handleTouchStart('right')}
									onTouchEnd={() => handleTouchEnd('right')}
									onMouseDown={() => handleTouchStart('right')}
									onMouseUp={() => handleTouchEnd('right')}
									onMouseLeave={() => handleTouchEnd('right')}
								>
									▶︎
								</button>
							</div>
						</div>
					</div>
				)}


			</div>
			{showInstructions && <InstructionsModal />}
			{gameState.lives <= 0 && <EndGameModal />}
		</div>
	);
}

export default ShaftGamePage;